/*
 * o16-header-taglines.js
 * Loaded by sections/header.liquid via a deferred <script> tag.
 *
 * FIT-TO-ONE-LINE for the burger drawer's per-item taglines
 * (.o16-nav-item-band__sub, the "/ {tagline}" spans rendered by
 * snippets/header-drawer.liquid from the o16_nav_subs textarea setting).
 *
 * GOVERNING RULE (owner round-4 2026-07-09): every tagline must read on ONE
 * line, with NO ellipsis truncation, and the FULL text must stay visible.
 * The only way to satisfy all three at once is to shrink each tagline's
 * font-size — from its normal CSS-declared size down to a floor — until the
 * whole string fits its available width. Short taglines are left at the
 * normal size (only the long ones shrink), so the row keeps a consistent
 * look wherever the text is already short enough.
 *
 * Pattern reused from assets/o16-collection-intro.js's fit-to-one-line
 * routine: force white-space:nowrap, measure scrollWidth vs clientWidth,
 * scale font-size proportionally, clamp to a floor.
 *
 * CRITICAL TIMING: the burger drawer's contents are native <details> UA
 * children, so they are not rendered (effectively display:none, zero
 * width) until the drawer's <details class="menu-drawer-container"> gains
 * its `open` attribute. Nothing here can be measured on page load. The fit
 * routine is instead triggered by:
 *   1. the native `toggle` event on that <details> element — fires every
 *      time the drawer opens OR closes; we act only when it opens.
 *   2. window resize / orientationchange, while the drawer is open.
 *   3. document.fonts.ready, in case a web-font swap changes text metrics
 *      while the drawer is already open.
 * A requestAnimationFrame retry loop guards against the rare case where the
 * `toggle` event fires a frame before layout has settled (e.g. iOS Safari).
 *
 * Vanilla JS, no dependencies. Token-driven CSS is never altered here beyond
 * the per-element font-size this routine computes.
 */
(function () {
  var SAFETY = 0.98;
  var MIN_REM = 0.7; /* font-size floor, in rem, below which text would be illegible */
  var RESIZE_DEBOUNCE_MS = 150;

  var drawerDetails = document.querySelector('header-drawer .menu-drawer-container');
  if (!drawerDetails) return; /* no burger drawer on this page */

  var retryFrame = null;

  function rootFontSizePx() {
    return parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 10;
  }

  /*
   * fitTagline — shrink one .o16-nav-item-band__sub to fit its available
   * width on a single line. Resets to the natural (CSS-declared) size first
   * so it can also GROW back when its cell has more room (e.g. after a
   * resize) — the element only ends up smaller than natural if it still
   * overflows at that size. Returns false if the element has zero
   * measurements yet (drawer not laid out / still transitioning in), so the
   * caller can retry rather than apply a bogus fit.
   */
  function fitTagline(el, minPx) {
    if (el.textContent.trim() === '') return true; /* nothing to fit */

    el.style.fontSize = ''; /* read the natural CSS-declared size */
    var natural = parseFloat(window.getComputedStyle(el).fontSize);
    if (!natural) return false;

    el.style.fontSize = natural + 'px';
    var textWidth = el.scrollWidth;
    var available = el.clientWidth;
    if (textWidth <= 0 || available <= 0) return false;

    if (textWidth > available) {
      var fitted = natural * (available / textWidth) * SAFETY;
      fitted = Math.max(minPx, Math.min(natural, fitted));
      el.style.fontSize = fitted + 'px';
    }
    return true;
  }

  function fitAll() {
    var minPx = MIN_REM * rootFontSizePx();
    var allOk = true;
    document.querySelectorAll('.o16-nav-item-band__sub').forEach(function (el) {
      if (!fitTagline(el, minPx)) allOk = false;
    });
    return allOk;
  }

  /* Retry via rAF until a pass measures cleanly (bounded by the drawer
     staying open — if it closes mid-retry there is simply nothing visible
     to mis-measure, and the next `toggle` open re-arms this). */
  function fitAllWithRetry() {
    if (retryFrame) window.cancelAnimationFrame(retryFrame);
    var ok = fitAll();
    if (!ok && drawerDetails.open) {
      retryFrame = window.requestAnimationFrame(fitAllWithRetry);
    }
  }

  var resizeTimer = null;
  function onResize() {
    if (!drawerDetails.open) return; /* nothing visible to re-fit */
    if (resizeTimer) window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(fitAllWithRetry, RESIZE_DEBOUNCE_MS);
  }

  /* 1. Drawer open/close — the primary trigger, since this is the first
        moment any tagline has a measurable width. */
  drawerDetails.addEventListener('toggle', function () {
    if (drawerDetails.open) fitAllWithRetry();
  });

  /* 2. Resize / orientation change while the drawer is open. */
  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', onResize);

  /* 3. Web-font swap while the drawer is already open. */
  if (document.fonts && typeof document.fonts.ready === 'object' &&
      typeof document.fonts.ready.then === 'function') {
    document.fonts.ready.then(function () {
      if (drawerDetails.open) fitAllWithRetry();
    });
  }
})();
